import React, { useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Image } from 'expo-image';
import { Camera, X } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useImagePicker } from '../../hooks/useImagePicker';

interface Props {
  /** URI of the primary (after) photo. Section is hidden until this is set. */
  imageUri: string | null;
  /** URI of the chosen before photo, or null when none selected. */
  value: string | null;
  onChange: (uri: string | null) => void;
}

/**
 * Optional "Add a before photo" row in the post composer.
 *
 * - Hidden until the primary photo is chosen (imageUri non-null).
 * - Collapsed by default; a tap opens the system image picker.
 * - When a before photo is chosen, shows a small thumbnail + remove control.
 * - Independent of structureId — no structure link required.
 */
export function BeforePhotoSection({ imageUri, value, onChange }: Props) {
  const { colors } = useTheme();
  const { showPicker } = useImagePicker();

  const handlePick = useCallback(() => {
    showPicker((uri) => onChange(uri));
  }, [showPicker, onChange]);

  const handleRemove = useCallback(() => {
    onChange(null);
  }, [onChange]);

  // Only show after the primary photo is selected.
  if (!imageUri) return null;

  return (
    <>
      {value ? (
        <View style={[styles.selectedRow, { borderColor: colors.border }]}>
          <Image
            source={value}
            style={[styles.selectedThumb, { backgroundColor: colors.border }]}
            contentFit="cover"
            cachePolicy="memory-disk"
          />
          <View style={styles.selectedText}>
            <Text style={[styles.selectedTitle, { color: colors.text }]}>Before photo added</Text>
            <Text style={[styles.selectedMeta, { color: colors.textSecondary }]}>
              Tap to change
            </Text>
          </View>
          <TouchableOpacity
            onPress={handlePick}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Change before photo">
            <Camera size={16} color={colors.textTertiary} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleRemove}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Remove before photo">
            <X size={16} color={colors.textTertiary} />
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity
          onPress={handlePick}
          activeOpacity={0.7}
          style={[styles.row, { borderColor: colors.border }]}>
          <Camera size={16} color={colors.text} />
          <Text style={[styles.rowText, { color: colors.textSecondary }]}>
            Add a before photo (optional)
          </Text>
        </TouchableOpacity>
      )}
      <Text style={[styles.hint, { color: colors.textTertiary }]}>
        {value
          ? 'This post will show as a before/after pair'
          : 'Pair this with an earlier photo to show progress'}
      </Text>
    </>
  );
}

const styles = StyleSheet.create({
  // Hairline-bordered rows on the canvas, matching the rest of the ported
  // create screen — these two were missed because they only render once a
  // primary photo is picked.
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: SPACING.smd,
    gap: SPACING.sm,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: StyleSheet.hairlineWidth,
  },
  rowText: {
    ...TYPOGRAPHY.bodySmall,
    flex: 1,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
    marginBottom: SPACING.md,
    marginLeft: SPACING.smd,
  },
  selectedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: SPACING.sm,
    gap: SPACING.sm,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: StyleSheet.hairlineWidth,
  },
  selectedThumb: {
    width: 40,
    height: 40,
    borderRadius: BORDER_RADIUS.sm,
  },
  selectedText: {
    flex: 1,
    gap: 1,
  },
  selectedTitle: {
    ...TYPOGRAPHY.label,
  },
  selectedMeta: {
    ...TYPOGRAPHY.bodySmall,
  },
});
